/**
 * Demo data for "Try Demo" mode
 * Realistic sample data for Email, Todo, and Notes panels
 */

import type { CachedEmail } from './email-store';
import type { Todo, TodoProject, TodoSection } from '@signa/shared';
import type { Note, NoteBlock } from '@signa/shared';


// Demo projects

export const DEMO_PROJECTS: TodoProject[] = [
  { id: 'proj-1', userId: 'demo-user', name: 'Work', color: 'var(--accent)', position: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'proj-2', userId: 'demo-user', name: 'Personal', color: 'var(--success)', position: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'proj-3', userId: 'demo-user', name: 'Health', color: 'var(--error)', position: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
];

export const DEMO_SECTIONS: TodoSection[] = [
  { id: 'sec-1', projectId: 'proj-1', name: 'Sprint 12', position: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  { id: 'sec-2', projectId: 'proj-1', name: 'Backlog', position: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
];

// Demo todos

export const DEMO_TODOS: Todo[] = [
  {
    id: 'todo-1',
    userId: 'demo-user',
    title: 'Review Q2 strategy deck before tomorrow standup',
    description: 'Sarah sent the updated version. Need to flag any concerns before the all-hands.',
    priority: 1,
    dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    isRecurring: false,
    projectId: 'proj-1',
    sectionId: 'sec-1',
    labels: ['strategy', 'work'],
    position: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'todo-2',
    userId: 'demo-user',
    title: 'Fix authentication timeout bug in production',
    description: 'Users getting logged out after 5 minutes. Hotfix needed before EOD.',
    priority: 1,
    isRecurring: false,
    projectId: 'proj-1',
    sectionId: 'sec-1',
    labels: ['bug', 'urgent'],
    position: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'todo-3',
    userId: 'demo-user',
    title: 'Prepare investor update email',
    description: 'Monthly update needed. Highlight growth metrics and upcoming feature launches.',
    priority: 2,
    dueDate: new Date(Date.now() + 172800000).toISOString().split('T')[0],
    isRecurring: false,
    projectId: 'proj-1',
    labels: ['communications'],
    position: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'todo-4',
    userId: 'demo-user',
    title: 'Weekly grocery run',
    description: '',
    priority: 3,
    dueDate: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
    isRecurring: true,
    recurrenceRule: 'FREQ=WEEKLY',
    projectId: 'proj-2',
    labels: ['home'],
    position: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'todo-5',
    userId: 'demo-user',
    title: 'Morning run - 5K',
    description: '',
    priority: 3,
    isRecurring: true,
    recurrenceRule: 'FREQ=WEEKLY;BYDAY=MO,WE,FR',
    projectId: 'proj-3',
    labels: ['fitness'],
    position: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'todo-6',
    userId: 'demo-user',
    title: 'Schedule dentist appointment',
    description: '6-month checkup overdue. Call Dr. Jensen office.',
    priority: 4,
    isRecurring: false,
    projectId: 'proj-3',
    labels: ['health'],
    position: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Demo emails

export const DEMO_EMAILS: CachedEmail[] = [
  {
    id: 'email-1',
    provider: 'gmail',
    threadId: 'thread-1',
    subject: 'RE: Q2 Strategy Deck - Updated',
    fromName: 'Sarah Chen',
    fromEmail: 'sarah.chen@work.co',
    toRecipients: [{ name: 'Demo User', email: 'demo@gmail.com' }],
    snippet: 'Hi, I have updated the deck based on your feedback from last week. Key changes: new market analysis section on page 12, revised revenue projections with the Q1 actuals, and a clearer timeline for the product launch. Please review before tomorrow standup...',
    receivedAt: new Date(Date.now() - 3600000).toISOString(),
    isRead: false,
    isStarred: true,
    hasAttachments: true,
    labels: [],
    category: 'people',
    isArchived: false,
    isSnoozed: false,
  },
  {
    id: 'email-2',
    provider: 'gmail',
    threadId: 'thread-2',
    subject: '[repo/work-app] Pull request #247: Fix auth timeout on mobile',
    fromName: 'GitHub',
    fromEmail: 'noreply@github.com',
    toRecipients: [{ name: 'Demo User', email: 'demo@gmail.com' }],
    snippet: 'The auth timeout issue on mobile devices has been resolved. The problem was in the token refresh logic - the refresh was not being triggered correctly when the app was backgrounded...',
    receivedAt: new Date(Date.now() - 7200000).toISOString(),
    isRead: false,
    isStarred: false,
    hasAttachments: false,
    labels: [],
    category: 'notif',
    isArchived: false,
    isSnoozed: false,
  },
  {
    id: 'email-3',
    provider: 'gmail',
    threadId: 'thread-3',
    subject: 'Your receipt from Acme Corp - $2,499.00',
    fromName: 'Stripe',
    fromEmail: 'receipts@stripe.com',
    toRecipients: [{ name: 'Demo User', email: 'demo@gmail.com' }],
    snippet: 'Thank you for your payment. This receipt confirms that your account has been charged $2,499.00 for the Enterprise plan (March 2026). Your next billing date is April 1, 2026...',
    receivedAt: new Date(Date.now() - 86400000).toISOString(),
    isRead: true,
    isStarred: false,
    hasAttachments: false,
    labels: [],
    category: 'transaction',
    isArchived: false,
    isSnoozed: false,
  },
  {
    id: 'email-4',
    provider: 'microsoft',
    threadId: 'thread-4',
    subject: 'Lunch next Tuesday?',
    fromName: 'Alex Kim',
    fromEmail: 'alex.kim@outlook.com',
    toRecipients: [{ name: 'Demo User', email: 'demo@outlook.com' }],
    snippet: 'Hey! Been a while since we caught up. Are you free for lunch next Tuesday around noon? There is a new Thai place near the office that came highly recommended...',
    receivedAt: new Date(Date.now() - 172800000).toISOString(),
    isRead: true,
    isStarred: false,
    hasAttachments: false,
    labels: [],
    category: 'people',
    isArchived: false,
    isSnoozed: false,
  },
  {
    id: 'email-5',
    provider: 'microsoft',
    threadId: 'thread-5',
    subject: 'The Verge Morning Report - AI chips, electric vehicles, and more',
    fromName: 'The Verge',
    fromEmail: 'newsletters@theverge.com',
    toRecipients: [{ name: 'Demo User', email: 'demo@outlook.com' }],
    snippet: "In today's edition: NVIDIA announces next-gen AI training chips with 3x performance improvement. Ford pushes EV truck production to 2027. Apple's foldable iPhone confirmed for 2027...",
    receivedAt: new Date(Date.now() - 259200000).toISOString(),
    isRead: true,
    isStarred: false,
    hasAttachments: false,
    labels: [],
    category: 'newsletter',
    isArchived: false,
    isSnoozed: false,
  },
];

// Demo notes

function makeBlock(type: NoteBlock['type'], content: string, id?: string): NoteBlock {
  return { id: id ?? `block-${Math.random().toString(36).slice(2)}`, type, content };
}

const DEMO_NOTE_BLOCKS_1: NoteBlock[] = [
  makeBlock('heading1', 'Q2 2026 Product Milestones'),
  makeBlock('heading2', 'April'),
  makeBlock('checkbox', 'Auth system v2 launch', 'cb-1'),
  makeBlock('checkbox', 'Mobile app beta release', 'cb-2'),
  makeBlock('checkbox', 'API rate limiting implementation', 'cb-3'),
  makeBlock('heading2', 'May'),
  makeBlock('checkbox', 'Enterprise SSO integration', 'cb-4'),
  makeBlock('checkbox', 'Advanced analytics dashboard', 'cb-5'),
  makeBlock('heading2', 'June'),
  makeBlock('checkbox', 'Internationalization (DE, FR, ES)', 'cb-6'),
  makeBlock('checkbox', 'SOC 2 compliance audit', 'cb-7'),
];

const DEMO_NOTE_BLOCKS_2: NoteBlock[] = [
  makeBlock('heading1', 'Sprint 11 Retrospective'),
  makeBlock('heading2', 'What went well'),
  makeBlock('bulletList', 'Velocity increased 15% vs Sprint 10'),
  makeBlock('bulletList', 'Code review turnaround improved'),
  makeBlock('bulletList', 'New CI pipeline saved ~20 min per PR'),
  makeBlock('heading2', 'What could improve'),
  makeBlock('bulletList', 'Frontend testing coverage (currently 34%)'),
  makeBlock('bulletList', 'Technical debt in the auth module'),
  makeBlock('heading2', 'Action items'),
  makeBlock('bulletList', 'Add Jest tests for auth flows - @alex, due Friday'),
  makeBlock('bulletList', 'Refactor token refresh logic - @jordan, this sprint'),
];

const DEMO_NOTE_BLOCKS_3: NoteBlock[] = [
  makeBlock('heading1', 'The Lean Startup - Eric Ries'),
  makeBlock('heading2', 'Core Principles'),
  makeBlock('paragraph', 'Build-Measure-Learn: The fundamental loop. Build something, measure how customers respond, learn whether to pivot or persevere.'),
  makeBlock('paragraph', 'MVP: Build the minimum thing that tests your riskiest assumption. Not a prototype, not a mockup - a real product with just enough features to be valuable.'),
  makeBlock('heading2', 'Key Quotes'),
  makeBlock('quote', 'A startup is an institution designed to iterate under uncertainty.'),
  makeBlock('quote', 'If you are not embarrassed by the first version of your product, you launched too late.'),
];

export const DEMO_NOTES: Note[] = [
  {
    id: 'note-1',
    userId: 'demo-user',
    title: 'Q2 Product Roadmap - Key Milestones',
    blocks: DEMO_NOTE_BLOCKS_1,
    encryptedContentJson: '',
    isPinned: false,
    isDaily: false,
    tags: ['product', 'roadmap'],
    linkedNoteIds: [],
    backlinkNoteIds: [],
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: 'note-2',
    userId: 'demo-user',
    title: 'Meeting Notes - Sprint Retrospective (April 7)',
    blocks: DEMO_NOTE_BLOCKS_2,
    encryptedContentJson: '',
    isPinned: false,
    isDaily: false,
    tags: ['meeting', 'sprint'],
    linkedNoteIds: [],
    backlinkNoteIds: [],
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    updatedAt: new Date(Date.now() - 604800000).toISOString(),
  },
  {
    id: 'note-3',
    userId: 'demo-user',
    title: 'Book Notes - "The Lean Startup"',
    blocks: DEMO_NOTE_BLOCKS_3,
    encryptedContentJson: '',
    isPinned: false,
    isDaily: false,
    tags: ['books', 'startup', 'learning'],
    linkedNoteIds: [],
    backlinkNoteIds: [],
    createdAt: new Date(Date.now() - 2592000000).toISOString(),
    updatedAt: new Date(Date.now() - 1209600000).toISOString(),
  },
];

// NoteSummary type (matches lib/notes-api)
interface DemoNoteSummary {
  id: string;
  title: string;
  snippet: string;
  updatedAt: string;
  labels: string[];
  isPinned: boolean;
  isDaily: boolean;
}

// NoteSummaries derived from notes
export const DEMO_NOTE_SUMMARIES: DemoNoteSummary[] = DEMO_NOTES.map((n) => ({
  id: n.id,
  title: n.title,
  snippet: n.blocks.find((b) => b.type === 'paragraph')?.content?.slice(0, 120) ?? '',
  updatedAt: n.updatedAt,
  labels: n.tags,
  isPinned: n.isPinned,
  isDaily: n.isDaily,
}));
